import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Globe,
  Mail,
  MapPin,
  Calendar,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_META = {
  facebook: { Icon: Facebook, label: "Facebook" },
  facebook_personal: { Icon: Facebook, label: "Facebook (Personal)" },
  instagram: { Icon: Instagram, label: "Instagram" },
  twitter: { Icon: Twitter, label: "X / Twitter" },
  youtube: { Icon: Youtube, label: "YouTube" },
  website: { Icon: Globe, label: "Website" },
  email: { Icon: Mail, label: "Email" },
  mixcloud: { Icon: Globe, label: "Mixcloud" },
  bluesky: { Icon: Globe, label: "Bluesky" },
  soundcloud: { Icon: Globe, label: "SoundCloud" },
  writing: { Icon: Globe, label: "Writing.Com" },
  podbean: { Icon: Globe, label: "Podbean" },
};

const DAY_LABELS = {
  sunday: "SUN",
  monday: "MON",
  tuesday: "TUE",
  wednesday: "WED",
  thursday: "THU",
  friday: "FRI",
  saturday: "SAT",
};

export default function DJProfile() {
  const { slug } = useParams();
  const [dj, setDj] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/djs/${slug}`);
        setDj(res.data);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="px-4 md:px-10 py-20 text-zinc-300 tracking-widest uppercase text-sm">
          //_loading profile...
        </div>
      </div>
    );
  }

  if (notFound || !dj) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="px-4 md:px-10 py-20">
          <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-3">
            //_404
          </div>
          <h1 className="font-display text-5xl md:text-7xl text-zinc-50 leading-none">
            DJ NOT FOUND<span className="text-red-600">.</span>
          </h1>
          <Link
            to="/djs"
            className="mt-6 inline-flex items-center gap-2 font-display tracking-widest uppercase text-red-500 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all DJs
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const socialEntries = Object.entries(dj.socials || {}).filter(
    ([k, v]) => v && SOCIAL_META[k]
  );

  return (
    <div className="relative min-h-screen" data-testid="dj-profile-page">
      <Header />

      {/* Back link */}
      <div className="border-b border-zinc-800 px-4 md:px-10 py-3">
        <Link
          to="/djs"
          className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-zinc-100 hover:text-red-500"
          data-testid="dj-back-link"
        >
          <ArrowLeft className="w-3 h-3" /> All Broadcasters
        </Link>
      </div>

      {/* Hero: photo + name */}
      <section className="border-b border-zinc-800 grid grid-cols-1 lg:grid-cols-12 gap-0">
        <div className="lg:col-span-5 border-r-0 lg:border-r border-zinc-800 bg-zinc-900 aspect-square lg:aspect-auto overflow-hidden">
          <DJPhoto dj={dj} />
        </div>
        <div className="lg:col-span-7 p-6 md:p-10 flex flex-col justify-center">
          <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-3">
            //_DJ_PROFILE
          </div>
          <h1
            className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight text-zinc-50"
            data-testid="dj-name"
          >
            {dj.name.toUpperCase()}
            <span className="text-red-600">.</span>
          </h1>
          {dj.location && (
            <div className="mt-4 flex items-center gap-2 text-sm text-zinc-100 tracking-widest uppercase">
              <MapPin className="w-4 h-4 text-red-500" /> {dj.location}
            </div>
          )}
          {dj.tagline && (
            <div className="mt-4 text-base md:text-lg text-zinc-300 leading-relaxed max-w-2xl">
              {dj.tagline}
            </div>
          )}
          {socialEntries.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {socialEntries.map(([platform, url]) => {
                const { Icon, label } = SOCIAL_META[platform];
                return (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border border-zinc-700 hover:border-red-600 hover:bg-red-600 hover:text-white px-3 py-2 text-xs tracking-widest uppercase text-zinc-300 transition-colors"
                    data-testid={`dj-social-${platform}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Bio */}
      <section className="border-b border-zinc-800 px-4 md:px-10 py-12 md:py-16">
        <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-4">
          //_BIO
        </div>
        <div
          className="max-w-3xl text-zinc-300 leading-relaxed whitespace-pre-line text-sm md:text-base"
          data-testid="dj-bio"
        >
          {dj.bio || "Bio coming soon — broadcaster is busy spinning records 🎸"}
        </div>
      </section>

      {/* Shows */}
      {dj.show_entries && dj.show_entries.length > 0 && (
        <section className="border-b border-zinc-800 px-4 md:px-10 py-12 md:py-16">
          <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-4">
            //_SHOWS · ON DRR
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-zinc-800">
            {dj.show_entries.map((show, idx) => (
              <div
                key={`${show.day}-${idx}`}
                className="border-r border-b border-zinc-800 p-5 bg-zinc-950"
                data-testid={`dj-show-${idx}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="text-xs tracking-widest text-zinc-300 uppercase">
                    <Calendar className="inline w-3 h-3 mr-1" />
                    {DAY_LABELS[show.day] || show.day.toUpperCase()} · {show.time}
                  </span>
                  {show.live && (
                    <span className="text-[10px] tracking-widest uppercase bg-red-600 text-black px-2 py-0.5 font-display">
                      ● LIVE
                    </span>
                  )}
                </div>
                <div className="font-display text-xl tracking-tight text-zinc-100">
                  {show.name}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

/**
 * DJPhoto — dj.photo with graceful onError fallback to the DJ's initial.
 * If the src fails to load (dead URL, hotlink block, 404) we swap in a
 * brutalist initial placeholder so the profile page never shows a broken
 * image icon.
 */
function DJPhoto({ dj }) {
  const [failed, setFailed] = useState(false);
  if (dj.photo && !failed) {
    return (
      <img
        src={dj.photo}
        alt={dj.name}
        className="w-full h-full object-cover"
        onError={() => setFailed(true)}
        data-testid="dj-photo"
      />
    );
  }
  return (
    <div
      className="w-full h-full min-h-[400px] flex items-center justify-center bg-gradient-to-br from-zinc-950 to-zinc-800"
      data-testid="dj-photo-fallback"
    >
      <span className="font-display text-9xl text-zinc-600 select-none">
        {dj.name[0]?.toUpperCase()}
      </span>
    </div>
  );
}

